import { calculerKpis } from './kpis.js';
import { pointsAnnuels, projeter } from './projection.js';
import { SCENARIOS } from './hypotheses.js';
import type {
  CodeScenario,
  FluxDate,
  FluxExceptionnel,
  FormeFrais,
  Kpis,
  PointSerie,
} from './types.js';

/** Inputs to compute all three scenarios of a simulation (aligns with `simuler`). */
export interface ParamsSimulation {
  readonly v0: number;
  readonly vm: number;
  readonly horizonAnnees: number;
  readonly fraisAnnuels: number;
  /** Gross annual return per scenario (from the active hypotheses version). */
  readonly rendementsBruts: Readonly<Record<CodeScenario, number>>;
  readonly flux?: readonly FluxExceptionnel[];
  /** Current month for KPI evaluation (RG-05). */
  readonly moisCourant: number;
  /** Validated contributions for the KPI/IRR (RG-06/RG-07). */
  readonly versementsValides: readonly FluxDate[];
  readonly formeFrais?: FormeFrais;
}

/** Full result: series, annual points and KPIs for the three scenarios. */
export interface ResultatSimulation {
  readonly series: Record<CodeScenario, PointSerie[]>;
  readonly pointsAnnuels: Record<CodeScenario, PointSerie[]>;
  readonly kpis: Record<CodeScenario, Kpis>;
  readonly hypothesesVersion: number;
}

/**
 * Compute the three scenarios in one pass so the chart scale is fixed and the PDF report
 * shares the exact same figures (RG-03). All scenarios reuse the same validated flows for
 * the KPI, only the projected capital differs.
 */
export function simulerTousScenarios(
  params: ParamsSimulation,
  hypothesesVersion: number,
): ResultatSimulation {
  const series = {} as Record<CodeScenario, PointSerie[]>;
  const annuels = {} as Record<CodeScenario, PointSerie[]>;
  const kpis = {} as Record<CodeScenario, Kpis>;

  for (const scenario of SCENARIOS) {
    const serie = projeter({
      v0: params.v0,
      vm: params.vm,
      horizonAnnees: params.horizonAnnees,
      fraisAnnuels: params.fraisAnnuels,
      rendementBrut: params.rendementsBruts[scenario],
      ...(params.flux ? { flux: params.flux } : {}),
      ...(params.formeFrais ? { formeFrais: params.formeFrais } : {}),
    });
    series[scenario] = serie;
    annuels[scenario] = pointsAnnuels(serie, params.horizonAnnees);
    kpis[scenario] = calculerKpis({
      serie,
      moisCourant: params.moisCourant,
      versementsValides: params.versementsValides,
    });
  }

  return { series, pointsAnnuels: annuels, kpis, hypothesesVersion };
}
